import type { JSONContent } from '@tiptap/react';
import type { CardUpsert } from '@yorukiri/shared';
import { api } from '../lib/api';

type CardBlockAttrs = {
  cardId: string | null;
  cardType: 'basic' | 'cloze' | 'mcq';
  front: string;
  back: string;
  options: string[];
  correct: number[];
  explanation: string;
};

export function collectCardBlocks(doc: JSONContent | null | undefined): CardBlockAttrs[] {
  if (!doc) return [];
  const out: CardBlockAttrs[] = [];
  const walk = (node: JSONContent) => {
    if (node.type === 'cardBlock' && node.attrs) {
      out.push(node.attrs as CardBlockAttrs);
    }
    node.content?.forEach(walk);
  };
  walk(doc);
  return out;
}

// Extract every distinct cloze group number in the text: {{c1::x}} → 1, etc.
function clozeGroups(text: string): number[] {
  const re = /\{\{c(\d+)::[^}]+\}\}/g;
  const set = new Set<number>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) set.add(Number(m[1]));
  return [...set].sort((a, b) => a - b);
}

// Cards require a stable id and at minimum a non-empty front; everything else
// is shape-dependent. Anything incomplete is skipped (but still lives in the
// doc — the user is presumably still typing).
//
// Multi-cloze expansion: a block with `{{c1::...}} {{c2::...}}` yields two
// card rows that share the block's cardId as their noteId. The first group's
// card keeps the block's id as its primary key; later groups are namespaced
// with a stable `<cardId>::c<n>` suffix so they survive edits idempotently.
function toUpserts(attrs: CardBlockAttrs): CardUpsert[] {
  if (!attrs.cardId || !attrs.front.trim()) return [];

  if (attrs.cardType === 'mcq') {
    const options = attrs.options.map((o) => o.trim()).filter(Boolean);
    const correct = attrs.correct
      .filter((i) => i < options.length && Boolean(options[i]))
      .sort((a, b) => a - b);
    if (options.length < 2 || correct.length === 0) return [];
    return [
      {
        id: attrs.cardId,
        type: 'mcq',
        front: attrs.front.trim(),
        back: (attrs.back || options[correct[0]!]!).trim(),
        noteId: attrs.cardId,
        extra: {
          options,
          correct,
          explanation: attrs.explanation.trim() || undefined,
        },
      },
    ];
  }

  if (attrs.cardType === 'cloze') {
    const groups = clozeGroups(attrs.front);
    if (groups.length === 0) return [];
    const noteId = attrs.cardId;
    return groups.map((g, idx) => ({
      id: idx === 0 ? attrs.cardId! : `${attrs.cardId}::c${g}`,
      type: 'cloze' as const,
      front: attrs.front.trim(),
      back: attrs.back.trim(),
      noteId,
      clozeGroup: g,
    }));
  }

  if (!attrs.back.trim()) return [];
  return [
    {
      id: attrs.cardId,
      type: 'basic',
      front: attrs.front.trim(),
      back: attrs.back.trim(),
      noteId: attrs.cardId,
    },
  ];
}

export async function syncCards(pageId: string, doc: JSONContent): Promise<void> {
  const blocks = collectCardBlocks(doc);
  const cards = blocks.flatMap(toUpserts);
  await api.post('/cards/sync', { pageId, cards });
}
